// KidApp — the entire UI a kid sees once they've PIN-logged-in.
//
// Replaces the adult App shell when `me.scope === "kid"`. Strips the
// chat/notes/food/todo/calendar/studio tabs down to two: Today (chore
// hero + ⭐ this week + meal preview) and Chores (full tile grid).
//
// Auth: kid sessions are issued by /api/kid-login with scope:"kid".
// Whoami still hands us the kid's display name, avatar color, and
// emoji — we drive the entire chrome's theming from those.
//
// Sign-out: bounces back to the kid-login picker (NOT the adult login)
// so siblings can hand off the tablet without an adult having to type
// a password. Implemented by calling auth.logout() then setting a
// query-param flag the LoginView reads on mount to re-enter kid mode.
//
// Future surfaces (notes/calendar/chat) ship in MR 4+.
//
// This file is intentionally NOT wired through the adult Topbar/MobileNav
// components — those carry a lot of adult-only chrome (search, user menu
// with admin options, tweaks panel) that we don't want a kid touching.

import { useEffect, useState } from "preact/hooks";
import "./styles/kid.css";

import { api } from "./lib/rpc";
import { useAuth } from "./lib/use-auth";
import { useMe, type Me } from "./lib/use-me";
import { KidTodayView } from "./sections/kid/today";
import { KidChoresView } from "./sections/kid/chores";
// Calendar no longer has its own tab — events render on Today (gated
// by the per-kid `calendar` feature flag). KidCalendarView is gone.
import { KidNotesView } from "./sections/kid/notes";
import { KidRewardsView } from "./sections/kid/rewards";
import { KidChatView } from "./sections/kid/chat";
import { KidTodoView } from "./sections/kid/todo";
import { Toast } from "./components/toast";
import { ConfirmHost } from "./components/confirm";
import { UpdateBanner } from "./components/update-banner";

type KidTab = "today" | "chores" | "rewards" | "journal" | "todo" | "chat";

export function KidApp() {
  const auth = useAuth();
  const me = useMe();
  const [tab, setTab] = useState<KidTab>("today");
  const [toast, setToast] = useState<string | null>(null);
  // Which surfaces a parent has enabled for this kid. Today + Chores are
  // always on; rewards/calendar/todo/journal/chat are gated (default off).
  // A disabled tab simply doesn't render; the chat view handles its own
  // quiet-hours / daily-limit states once chat itself is on.
  const [surfaces, setSurfaces] = useState<Record<string, boolean>>({ today: true, chores: true });
  const can = (s: string) => s === "today" || s === "chores" || !!surfaces[s];

  useEffect(() => {
    api.kidSurfaces().then((s) => setSurfaces({ today: true, chores: true, ...s })).catch(() => {});
  }, []);
  // If the kid's open tab gets turned off, bounce them home.
  useEffect(() => { if (!can(tab)) setTab("today"); }, [surfaces, tab]);

  // Set the per-kid CSS variable so the gradient backgrounds and accent
  // colors throughout the kid surfaces pull from their avatarColor. Falls
  // back to the accent default if the kid hasn't been given a color.
  useEffect(() => {
    if (!me) return;
    document.documentElement.style.setProperty("--kid-color", me.avatarColor || "var(--accent)");
    return () => {
      document.documentElement.style.removeProperty("--kid-color");
    };
  }, [me?.avatarColor]);

  const notify = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 1800);
  };

  const onSignOut = async () => {
    // Prefer dropping back to the household's shared device session so the
    // PIN picker reappears (siblings hand off without an adult). A full
    // reload re-bootstraps the shell into the kid-device scope → picker.
    try {
      const mode = await api.kidSwitch();
      if (mode === "device") {
        window.location.assign("/");
        return;
      }
    } catch {
      // fall through to the logout path below
    }
    // No device account (legacy LAN kid) or switch failed: full logout,
    // then re-enter kid-login mode rather than the adult password screen.
    await auth.logout();
    const url = new URL(window.location.href);
    url.searchParams.set("kid", "1");
    window.location.assign(url.toString());
  };

  if (!me) {
    return (
      <div className="kid-loading">loading…</div>
    );
  }

  return (
    <div className="kid-app">
      <header className="kid-topbar">
        <div className="kid-topbar-left">
          <div
            className="kid-topbar-avatar"
            style={{ background: me.avatarColor || "var(--accent)" }}
          >
            {me.avatarEmoji || me.initial}
          </div>
          <div className="kid-topbar-name">{me.displayName}</div>
        </div>
        <button
          type="button"
          className="kid-topbar-signout"
          onClick={onSignOut}
          title="Switch user"
        >
          Switch
        </button>
      </header>

      {/* Android APK update prompt — mounted above .kid-main so it's
        * visible. Previously rendered at the end of the layout, where
        * .kid-app's `overflow: hidden` + .kid-main's `flex: 1` pushed
        * it off the bottom of the viewport (and the fixed bottom nav
        * would have covered it anyway). Wrapping in a thin shell so
        * the banner takes its natural height and .kid-main can still
        * fill the remainder. */}
      <div className="kid-update-banner-slot">
        <UpdateBanner notify={notify} />
      </div>

      <main className={`kid-main${tab === "chat" ? " kid-main-chat" : ""}`}>
        {tab === "today" && (
          <KidTodayView me={me} notify={notify} onGoChores={() => setTab("chores")} />
        )}
        {tab === "chores" && (
          <KidChoresView me={me} notify={notify} />
        )}
        {tab === "rewards" && can("rewards") && (
          <KidRewardsView notify={notify} />
        )}
        {tab === "todo" && can("todo") && (
          <KidTodoView notify={notify} />
        )}
        {tab === "journal" && can("journal") && (
          <KidNotesView notify={notify} />
        )}
        {tab === "chat" && can("chat") && (
          <KidChatView me={me} notify={notify} />
        )}
      </main>

      <nav className="kid-nav">
        <button
          type="button"
          className={`kid-nav-btn${tab === "today" ? " is-active" : ""}`}
          onClick={() => setTab("today")}
        >
          <span className="kid-nav-icon">🏠</span>
          <span className="kid-nav-label">Today</span>
        </button>
        <button
          type="button"
          className={`kid-nav-btn${tab === "chores" ? " is-active" : ""}`}
          onClick={() => setTab("chores")}
        >
          <span className="kid-nav-icon">⭐</span>
          <span className="kid-nav-label">Chores</span>
        </button>
        {can("rewards") && (
          <button
            type="button"
            className={`kid-nav-btn${tab === "rewards" ? " is-active" : ""}`}
            onClick={() => setTab("rewards")}
          >
            <span className="kid-nav-icon">🎁</span>
            <span className="kid-nav-label">Rewards</span>
          </button>
        )}
        {can("todo") && (
          <button
            type="button"
            className={`kid-nav-btn${tab === "todo" ? " is-active" : ""}`}
            onClick={() => setTab("todo")}
          >
            <span className="kid-nav-icon">✅</span>
            <span className="kid-nav-label">To-do</span>
          </button>
        )}
        {can("journal") && (
          <button
            type="button"
            className={`kid-nav-btn${tab === "journal" ? " is-active" : ""}`}
            onClick={() => setTab("journal")}
          >
            <span className="kid-nav-icon">📓</span>
            <span className="kid-nav-label">Journal</span>
          </button>
        )}
        {can("chat") && (
          <button
            type="button"
            className={`kid-nav-btn${tab === "chat" ? " is-active" : ""}`}
            onClick={() => setTab("chat")}
          >
            <span className="kid-nav-icon">💬</span>
            <span className="kid-nav-label">Chat</span>
          </button>
        )}
      </nav>

      <Toast msg={toast} />
      <ConfirmHost />
    </div>
  );
}

export type { Me };
// Re-exports so the api type doesn't have to be re-imported in kid sub-
// components — kept here intentionally for any kid-only helpers we add.
export { api };
